/**
 * City models (ADR-0076): houses that multiply with the city's size and change style with
 * its owner's era, roofs and a flag in the owner's colour, walls when built, a few key
 * buildings, and its wonders as landmarks. The layout is seeded by the tile, so no two
 * cities look alike and a city keeps its look from turn to turn.
 */

import * as T from 'three';
import { COL, kit, mat, shadeOf, type Kit } from './parts.js';

export interface CitySpec {
  population: number;
  /** 0 ancient … 5 modern. */
  era: number;
  /** Building content keys the city holds (wonders included). */
  buildings: readonly string[];
  capital: boolean;
  seed: number;
}

/** The buildings that show on the map, besides walls and wonders. */
export const SHOWN_BUILDINGS = ['palace', 'temple', 'cathedral', 'market', 'factory', 'power_plant', 'observatory', 'city_walls'];

const WALL = [0xd9c7a0, 0xf1ece0, 0xe8dcc4, 0xeee4d0, 0xa8604c, 0xc9d3da] as const;
const STONE = 0xbdb6a6;
const GLASS = 0x9cc3d3;

function rng(seed: number): () => number {
  let a = seed * 2654435761 + 0x9e3779b9;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A gabled roof over a w×d footprint, ridge along x. */
function gable(k: Kit, x: number, y: number, z: number, w: number, d: number, h: number, colour: number): void {
  const m = k.shape([[-d / 2 - 0.25, 0], [d / 2 + 0.25, 0], [0, h]], w + 0.3, colour, 0, 0, 0);
  m.rotation.y = Math.PI / 2;
  m.position.set(x - w / 2 - 0.15, y, z);
}

/** One house in the era's style. */
function house(k: Kit, civ: number, era: number, x: number, z: number, w: number, d: number, storeys: number, turn: number): void {
  const g = new T.Group();
  g.position.set(x, 0, z);
  g.rotation.y = turn;
  k.add(g);
  const h = kit(g);
  const height = 1.6 + storeys * 1.5;
  const wall = WALL[era]!;
  if (era === 5) {
    // Modern: a glass and concrete block, a roof cap in the owner's colour.
    h.box(w, height, d, 0, height / 2, 0, 0xc9d3da);
    for (let y = 1.2; y < height - 0.6; y += 1.1) h.box(w + 0.04, 0.55, d + 0.04, 0, y, 0, GLASS);
    h.box(w + 0.2, 0.35, d + 0.2, 0, height + 0.15, 0, civ);
    return;
  }
  h.box(w, height, d, 0, height / 2, 0, wall);
  // Windows and a door on the two faces the camera sees.
  for (let s = 0; s < storeys; s++) {
    const y = 1.6 + s * 1.5 + 0.2;
    for (let i = -1; i <= 1; i += 2) {
      h.box(0.4, 0.55, 0.05, (i * w) / 4, y, d / 2 + 0.01, COL.DARK);
      h.box(0.05, 0.55, 0.4, w / 2 + 0.01, y, (i * d) / 4, COL.DARK);
    }
  }
  h.box(0.6, 1.1, 0.05, 0, 0.55, d / 2 + 0.01, era >= 2 ? COL.WOODD : 0x6a5038);
  if (era === 2) {
    // Medieval timber framing.
    for (const fx of [-w / 2, 0, w / 2]) h.box(0.12, height, 0.06, fx, height / 2, d / 2 + 0.02, COL.WOODD);
    h.box(w, 0.12, 0.06, 0, height * 0.55, d / 2 + 0.02, COL.WOODD);
  }
  if (era === 0) {
    // Ancient: flat roof with a parapet and an awning in the owner's colour.
    h.box(w + 0.2, 0.25, d + 0.2, 0, height + 0.12, 0, shadeOf(wall, 0.92));
    h.box(w * 0.6, 0.12, d * 0.5, -w * 0.1, height + 0.85, 0, civ);
    for (const [px, pz] of [[-w * 0.4, -d * 0.25], [-w * 0.4, d * 0.25], [w * 0.2, -d * 0.25], [w * 0.2, d * 0.25]] as Array<[number, number]>)
      h.bar([px, height + 0.2, pz], [px, height + 0.8, pz], 0.06, 0.06, COL.WOOD, 4);
    return;
  }
  const pitch = era === 2 ? 2.2 : era === 4 ? 1.0 : 1.4;
  gable(h, 0, height, 0, w, d, pitch, civ);
  if (era === 4 && storeys > 1) h.box(0.5, 2.0, 0.5, w / 4, height + 1.0, -d / 4, 0x7a3a2a);
}

/** The town's central building: a palace in the capital, otherwise a hall or keep. */
function centre(k: Kit, civ: number, era: number, capital: boolean): number {
  const wall = era === 4 ? 0xb46a52 : era === 5 ? 0xdde4e8 : STONE;
  if (era === 5) {
    const h = capital ? 14 : 9;
    k.box(3.4, h, 3.4, 0, h / 2, 0, 0xc9d3da);
    for (let y = 1; y < h - 0.4; y += 1.0) k.box(3.45, 0.5, 3.45, 0, y, 0, GLASS);
    k.box(2.4, 2.0, 2.4, 0, h + 1, 0, 0xc9d3da);
    k.bar([0, h + 2, 0], [0, h + 5, 0], 0.08, 0.08, COL.IRON, 4);
    return h + 2;
  }
  if (capital) {
    k.box(6.0, 4.0, 4.4, 0, 2.0, 0, wall);
    for (let x = -2.4; x <= 2.4; x += 1.2) k.bar([x, 0, 2.3], [x, 3.6, 2.3], 0.22, 0.22, COL.WHITE, 8);
    k.box(6.4, 0.4, 4.8, 0, 4.2, 0, shadeOf(wall, 0.9));
    if (era >= 2) {
      k.bar([0, 4.4, 0], [0, 5.6, 0], 1.7, 1.7, wall, 12);
      const dome = k.ball(1.75, 0, 5.6, 0, civ, 2, 1, 0.8, 1);
      void dome;
      return 8.4;
    }
    k.shape([[-2.6, 0], [2.6, 0], [0, 1.4]], 0.4, civ, 0, 4.4, 2.0);
    gable(k, 0, 4.4, 0, 6.0, 4.4, 1.4, civ);
    return 6.2;
  }
  // A tower or keep.
  const height = era <= 1 ? 6.0 : 8.0;
  k.box(2.8, height, 2.8, 0, height / 2, 0, wall);
  for (const [cx, cz] of [[-1.1, -1.1], [-1.1, 1.1], [1.1, -1.1], [1.1, 1.1], [0, 1.1], [1.1, 0]] as Array<[number, number]>)
    k.box(0.6, 0.7, 0.6, cx, height + 0.35, cz, wall);
  k.box(0.5, 0.8, 0.05, 0, height - 1.6, 1.41, COL.DARK);
  return height + 0.7;
}

function flag(k: Kit, civ: number, x: number, y: number, z: number): void {
  k.bar([x, y, z], [x, y + 3.2, z], 0.07, 0.07, COL.WOODD, 4);
  k.shape([[0, 0], [2.0, -0.2], [1.6, -0.7], [2.0, -1.2], [0, -1.2]], 0, civ, x, y + 3.1, z);
}

function walls(k: Kit, era: number, radius: number): void {
  const colour = era === 0 ? 0xcbb48a : era >= 4 ? 0x9a9488 : STONE;
  const sides = 8;
  for (let i = 0; i < sides; i++) {
    const a = ((i + 0.5) / sides) * Math.PI * 2;
    const b = ((i + 1.5) / sides) * Math.PI * 2;
    const p = [Math.cos(a) * radius, Math.sin(a) * radius];
    const q = [Math.cos(b) * radius, Math.sin(b) * radius];
    // The gate side (towards the camera) is left open.
    if (i === 1) continue;
    const mx = (p[0]! + q[0]!) / 2;
    const mz = (p[1]! + q[1]!) / 2;
    const len = Math.hypot(q[0]! - p[0]!, q[1]! - p[1]!);
    const seg = k.box(len, 1.8, 0.7, mx, 0.9, mz, colour);
    seg.rotation.y = -Math.atan2(q[1]! - p[1]!, q[0]! - p[0]!);
    for (let t = 0.1; t < 1; t += 0.2) {
      const c = k.box(0.45, 0.5, 0.75, p[0]! + (q[0]! - p[0]!) * t, 2.05, p[1]! + (q[1]! - p[1]!) * t, colour);
      c.rotation.y = seg.rotation.y;
    }
    k.bar([q[0]!, 0, q[1]!], [q[0]!, 2.8, q[1]!], 0.85, 0.95, colour, 8);
    k.bar([q[0]!, 2.8, q[1]!], [q[0]!, 3.2, q[1]!], 1.0, 1.0, shadeOf(colour, 0.9), 8);
  }
}

/** Key buildings, each a small recognisable model at the given spot. */
const KEY: Record<string, (k: Kit, civ: number, era: number) => void> = {
  temple(k, civ) {
    k.box(4.0, 0.5, 2.8, 0, 0.25, 0, STONE);
    for (let x = -1.6; x <= 1.6; x += 0.8) for (const z of [1.1, -1.1]) k.bar([x, 0.5, z], [x, 2.8, z], 0.16, 0.16, COL.WHITE, 8);
    k.box(4.0, 0.35, 2.8, 0, 2.95, 0, COL.WHITE);
    gable(k, 0, 3.1, 0, 4.0, 2.8, 1.0, civ);
  },
  cathedral(k, civ) {
    k.box(5.0, 3.6, 2.4, 0, 1.8, 0, STONE);
    gable(k, 0, 3.6, 0, 5.0, 2.4, 1.6, civ);
    k.box(1.6, 6.0, 1.6, 2.6, 3.0, 0, STONE);
    k.cone([2.6, 6.0, 0], [2.6, 9.4, 0], 1.0, civ, 4);
    k.disc(0.5, 12, GLASS, 2.6, 4.4, 0.81, 0);
  },
  market(k, civ) {
    for (const [x, z] of [[-1.4, -0.8], [1.2, -0.6], [-0.2, 1.2]] as Array<[number, number]>) {
      for (const [px, pz] of [[-0.6, -0.5], [0.6, -0.5], [-0.6, 0.5], [0.6, 0.5]] as Array<[number, number]>) k.bar([x + px, 0, z + pz], [x + px, 1.4, z + pz], 0.05, 0.05, COL.WOOD, 4);
      k.box(1.5, 0.12, 1.3, x, 1.45, z, civ);
      k.box(1.0, 0.5, 0.7, x, 0.25, z, 0xc4a77a);
    }
  },
  factory(k, civ) {
    k.box(5.0, 2.4, 3.2, 0, 1.2, 0, 0xa8604c);
    for (let x = -2.0; x <= 2.0; x += 1.0) k.shape([[0, 0], [0, 1.0], [1.0, 0]], 3.2, x === 0 ? civ : 0x7a8088, x - 0.5, 2.4, -1.6);
    k.bar([-1.6, 2.4, -1.0], [-1.6, 7.4, -1.0], 0.45, 0.55, 0x8a4a3a, 8);
    k.ball(0.8, -1.5, 8.0, -1.0, 0xd9d9d9, 1, 1.2, 0.7, 1);
  },
  power_plant(k) {
    k.bar([0, 0, 0], [0, 5.0, 0], 1.6, 2.2, 0xd5d8da, 14);
    k.bar([0, 5.0, 0], [0, 5.6, 0], 1.7, 1.6, 0xd5d8da, 14);
    k.ball(1.4, 0, 6.6, 0, 0xf2f2f2, 1, 1.2, 0.6, 1.2);
    k.box(2.2, 1.6, 1.6, 2.4, 0.8, 0.8, 0xc9d3da);
  },
  observatory(k, civ) {
    k.bar([0, 0, 0], [0, 2.4, 0], 1.3, 1.4, COL.WHITE, 12);
    k.ball(1.3, 0, 2.4, 0, 0xc9cfd3, 1, 1, 0.9, 1);
    k.box(0.4, 1.2, 1.5, 0.5, 3.0, 0, COL.DARK, 0, 0, -0.5);
    k.box(2.8, 0.2, 2.8, 0, 0.1, 0, civ);
  },
};

/** Wonders: a small landmark model each. */
const WONDERS: Record<string, (k: Kit, civ: number) => void> = {
  ziggurat_of_ur(k) {
    for (let i = 0; i < 4; i++) k.box(5.0 - i * 1.2, 1.1, 5.0 - i * 1.2, 0, 0.55 + i * 1.1, 0, shadeOf(0xd2b48a, 1 - i * 0.04));
    k.box(0.8, 4.4, 0.6, 0, 2.2, 2.6, 0xc4a77a, -0.5);
  },
  library_of_ashurbanipal(k, civ) {
    k.box(4.4, 2.6, 3.0, 0, 1.3, 0, 0xd9c7a0);
    for (let x = -1.6; x <= 1.6; x += 0.8) k.box(0.35, 2.6, 0.2, x, 1.3, 1.55, 0xc4a77a);
    k.box(4.6, 0.4, 3.2, 0, 2.8, 0, civ);
  },
  terracotta_army(k) {
    for (let r = 0; r < 3; r++) for (let c = 0; c < 5; c++) {
      k.bar([c * 0.9 - 1.8, 0, r * 0.9 - 0.9], [c * 0.9 - 1.8, 1.0, r * 0.9 - 0.9], 0.22, 0.28, 0xb5704a, 6);
      k.ball(0.18, c * 0.9 - 1.8, 1.15, r * 0.9 - 0.9, 0xb5704a, 0);
    }
  },
  theatre_of_epidaurus(k) {
    for (let i = 0; i < 4; i++) {
      const m = new T.Mesh(new T.CylinderGeometry(3.2 - i * 0.55, 3.2 - i * 0.55, 0.45, 16, 1, false, Math.PI * 0.75, Math.PI * 1.5), mat(0xe0d6c0));
      m.position.set(0, 0.22 + i * 0.45, 0);
      k.add(m);
    }
    k.bar([0, 0, 0], [0, 0.1, 0], 1.1, 1.1, 0xc9b88e, 12);
  },
  house_of_wisdom(k, civ) {
    k.box(4.0, 2.6, 4.0, 0, 1.3, 0, 0xe8dcc4);
    k.bar([0, 2.6, 0], [0, 3.2, 0], 1.6, 1.6, 0xe8dcc4, 12);
    k.ball(1.6, 0, 3.2, 0, civ, 2, 1, 1.1, 1);
    k.bar([1.8, 0, 1.8], [1.8, 6.0, 1.8], 0.35, 0.4, 0xe8dcc4, 8);
  },
  angkor_wat(k) {
    k.box(5.0, 1.0, 5.0, 0, 0.5, 0, 0x9a9182);
    for (const [x, z, h] of [[0, 0, 5.4], [-1.8, -1.8, 3.6], [1.8, -1.8, 3.6], [-1.8, 1.8, 3.6], [1.8, 1.8, 3.6]] as Array<[number, number, number]>) {
      k.bar([x, 1.0, z], [x, 1.0 + h, z], 0.15, 0.75, 0x8f8676, 8);
    }
  },
  grand_bazaar(k, civ) {
    k.box(5.4, 1.8, 3.6, 0, 0.9, 0, 0xe8dcc4);
    for (let x = -1.8; x <= 1.8; x += 1.2) for (const z of [-0.9, 0.9]) k.ball(0.6, x, 1.8, z, x === 0 ? civ : 0xc9bfa8, 1, 1, 0.7, 1);
  },
  crystal_palace(k) {
    k.box(6.0, 2.4, 2.6, 0, 1.2, 0, GLASS);
    const vault = new T.Mesh(new T.CylinderGeometry(1.3, 1.3, 6.0, 12, 1, false, 0, Math.PI), mat(0xb8d6e2));
    vault.rotation.z = Math.PI / 2;
    vault.position.set(0, 2.4, 0);
    k.add(vault);
    for (let x = -2.6; x <= 2.6; x += 0.65) k.box(0.06, 2.4, 2.65, x, 1.2, 0, COL.WHITE);
  },
  uraniborg(k, civ) {
    k.box(3.0, 3.0, 3.0, 0, 1.5, 0, 0xc9a06a);
    gable(k, 0, 3.0, 0, 3.0, 3.0, 1.2, civ);
    for (const x of [-1.9, 1.9]) {
      k.bar([x, 0, 0], [x, 3.4, 0], 0.7, 0.7, 0xc9a06a, 10);
      k.ball(0.75, x, 3.4, 0, 0x8aa0a8, 1, 1, 0.9, 1);
    }
  },
  st_peters_basilica(k, civ) {
    k.box(5.0, 2.8, 3.4, 0, 1.4, 0, 0xeee4d0);
    for (let x = -1.8; x <= 1.8; x += 0.9) k.bar([x, 0, 1.75], [x, 2.6, 1.75], 0.18, 0.18, COL.WHITE, 8);
    k.bar([0, 2.8, 0], [0, 3.8, 0], 1.5, 1.5, 0xeee4d0, 14);
    k.ball(1.5, 0, 3.8, 0, 0x8aa0a8, 2, 1, 1.25, 1);
    k.bar([0, 5.6, 0], [0, 6.4, 0], 0.3, 0.3, civ, 8);
  },
  venetian_arsenal(k, civ) {
    for (const x of [-1.6, 1.6]) {
      k.box(1.4, 4.4, 1.4, x, 2.2, 0, 0xb46a52);
      k.box(1.6, 0.4, 1.6, x, 4.6, 0, 0xa05a44);
    }
    k.box(1.8, 0.8, 1.2, 0, 3.4, 0, 0xb46a52);
    k.box(1.8, 0.1, 1.2, 0, 0.05, 0.4, GLASS);
    k.shape([[0, 0], [1.6, -0.2], [0, -0.9]], 0, civ, 0, 5.8, 0);
    k.bar([0, 4.8, 0], [0, 5.9, 0], 0.05, 0.05, COL.WOODD, 4);
  },
  suez_canal(k, civ) {
    k.box(7.0, 0.1, 1.4, 0, 0.05, 0, 0x6f9bb8);
    for (const z of [0.9, -0.9]) k.box(7.0, 0.5, 0.4, 0, 0.25, z, 0xd9c7a0);
    k.bar([3.0, 0, 1.6], [3.0, 3.6, 1.6], 0.35, 0.5, COL.WHITE, 8);
    k.bar([3.0, 3.6, 1.6], [3.0, 4.2, 1.6], 0.42, 0.42, civ, 8);
  },
  eiffel_tower(k) {
    for (const [x, z] of [[-1.6, -1.6], [1.6, -1.6], [-1.6, 1.6], [1.6, 1.6]] as Array<[number, number]>) k.bar([x, 0, z], [x * 0.25, 5.0, z * 0.25], 0.18, 0.26, 0x7a5c4a, 4);
    k.box(2.2, 0.25, 2.2, 0, 2.0, 0, 0x7a5c4a);
    k.box(1.2, 0.2, 1.2, 0, 5.0, 0, 0x7a5c4a);
    k.bar([0, 5.0, 0], [0, 10.6, 0], 0.4, 0.1, 0x7a5c4a, 4);
  },
  empire_state_building(k, civ) {
    k.box(3.2, 6.0, 3.2, 0, 3.0, 0, 0xd9d4c8);
    k.box(2.4, 4.0, 2.4, 0, 8.0, 0, 0xd9d4c8);
    k.box(1.6, 2.4, 1.6, 0, 11.2, 0, 0xd9d4c8);
    k.cone([0, 12.4, 0], [0, 15.2, 0], 0.5, 0xc9cfd3, 8);
    for (let y = 0.8; y < 12; y += 0.9) k.box(y < 6 ? 3.24 : y < 10 ? 2.44 : 1.64, 0.35, y < 6 ? 3.24 : y < 10 ? 2.44 : 1.64, 0, y, 0, GLASS);
    k.box(0.7, 0.3, 0.7, 0, 12.5, 0, civ);
  },
  sydney_opera_house(k) {
    k.box(6.0, 0.8, 3.4, 0, 0.4, 0, 0xc9a882);
    for (const [x, s] of [[-1.8, 1.4], [0, 1.8], [1.8, 1.2]] as Array<[number, number]>) {
      const shell = new T.Mesh(new T.SphereGeometry(s * 1.2, 10, 6, 0, Math.PI, 0, Math.PI / 2), mat(COL.WHITE, true));
      shell.position.set(x, 0.8, 0);
      shell.rotation.y = -Math.PI / 2;
      shell.scale.set(0.9, 1.6, 1);
      k.add(shell);
    }
  },
  cern(k, civ) {
    const ring = new T.Mesh(new T.TorusGeometry(3.4, 0.25, 6, 32), mat(0x8a949c));
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.2;
    k.add(ring);
    k.box(2.6, 1.6, 1.8, 0, 0.8, 0, COL.WHITE);
    k.ball(1.0, 0, 1.6, 0, civ, 1, 1.3, 0.5, 1);
  },
};

export function buildCity(g: T.Group, civ: number, spec: CitySpec): void {
  const k = kit(g);
  const random = rng(spec.seed);
  const era = Math.max(0, Math.min(5, spec.era));
  const has = new Set(spec.buildings);

  // Paving under the town.
  const ground = new T.Mesh(new T.CylinderGeometry(9.6, 9.6, 0.12, 6), mat(era >= 4 ? 0xb8b4aa : 0xd8ccb0));
  ground.rotation.y = Math.PI / 6;
  ground.position.y = 0.06;
  g.add(ground);

  const top = centre(k, civ, era, spec.capital);
  flag(k, civ, spec.capital && era < 5 ? 2.4 : 0.8, top, spec.capital && era < 5 ? 1.6 : 0.8);

  // Places round the centre: wonders and key buildings take the first, houses the rest.
  const spots: Array<[number, number]> = [];
  for (const [r, n] of [[4.6, 6], [7.4, 9]] as Array<[number, number]>) {
    const offset = random() * Math.PI * 2;
    for (let i = 0; i < n; i++) {
      const a = offset + (i / n) * Math.PI * 2;
      spots.push([Math.cos(a) * r + (random() - 0.5) * 0.8, Math.sin(a) * r + (random() - 0.5) * 0.8]);
    }
  }
  // Wonders take the outer ring's front places, where nothing stands before them.
  const outer = spots.slice(6).sort((p, q) => q[1] - p[1]);
  const inner = spots.slice(0, 6);
  const used = new Set<[number, number]>();

  const place = (build: (k: Kit, civ: number, era: number) => void, spot: [number, number] | undefined, scale: number): void => {
    if (!spot) return;
    used.add(spot);
    const p = new T.Group();
    p.position.set(spot[0], 0, spot[1]);
    p.scale.setScalar(scale);
    p.rotation.y = Math.round(random() * 3) * (Math.PI / 2) * 0.25;
    g.add(p);
    build(kit(p), civ, era);
  };

  const wonders = spec.buildings.filter((b) => b in WONDERS).slice(0, 3);
  wonders.forEach((w, i) => place(WONDERS[w]!, outer[[0, 2, 1][i]!], 1.25));
  // Keep the places just behind a wonder clear, so it is not lost in the houses.
  if (wonders.length > 0) for (const spot of inner.filter((p) => p[1] > 1.5)) used.add(spot);
  const keys = SHOWN_BUILDINGS.filter((b) => b !== 'palace' && b !== 'city_walls' && has.has(b) && KEY[b]).slice(0, 3);
  keys.forEach((b, i) => place(KEY[b]!, inner[i * 2], 0.75));

  // Houses: more as the city grows, taller in later eras and bigger cities.
  const count = Math.min(spots.length, 3 + Math.round(spec.population * 0.75));
  let made = 0;
  for (const spot of [...inner, ...outer]) {
    if (made >= count) break;
    if (used.has(spot)) continue;
    const storeys = 1 + (spec.population >= 8 && random() < 0.5 ? 1 : 0) + (era >= 4 && spec.population >= 12 && random() < 0.5 ? 1 : 0);
    const w = 1.8 + random() * 1.2;
    const d = 1.6 + random() * 0.9;
    const tall = era === 5 ? Math.max(1, Math.round((spec.population / 6) * (0.6 + random() * 0.8))) : storeys;
    house(k, civ, era, spot[0], spot[1], w, d, tall, Math.atan2(spot[1], spot[0]) + Math.PI / 2);
    made++;
  }

  if (has.has('city_walls')) walls(k, era, 10.2);
}
